import { Trans, useLingui } from '@lingui/react/macro'
import { csatHooks } from '$src/lib/trpcHooks'
import { useCompanyCustomers } from '$src/lib/hooks/useCompanyCustomers'
import { Card, Center, Group, Loader, Stack, Text } from '@mantine/core'
import { AppPage } from '@markku/components'
import { IconBuilding, IconChevronRight } from '@tabler/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { CsatChip } from '$src/components/company-portal/CsatChip'
import { UserEmailBadge } from '$src/components/company-portal/UserEmailBadge'

function CompanyPortalIndex() {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { companies: companyCustomers, isLoading } = useCompanyCustomers()

  useEffect(() => {
    if (!isLoading && companyCustomers.length === 1) {
      navigate({
        to: `/company-portal/${companyCustomers[0].id}`,
        replace: true,
      })
    }
  }, [isLoading, companyCustomers, navigate])

  if (isLoading || companyCustomers.length === 1) {
    return (
      <AppPage
        title={t`Business accounts`}
        showBackButton={false}
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Loader size="md" />
        </Center>
      </AppPage>
    )
  }

  if (companyCustomers.length === 0) {
    return (
      <AppPage
        title={t`Business accounts`}
        showBackButton={false}
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Text c="dimmed">
            <Trans>No company accounts found</Trans>
          </Text>
        </Center>
      </AppPage>
    )
  }

  return (
    <AppPage
      title={t`Business accounts`}
      showBackButton={false}
      rightSection={<UserEmailBadge />}
    >
      <Stack gap="md">
        {companyCustomers.map((company) => (
          <Card
            key={company.id}
            withBorder
            radius="md"
            p="md"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate({ to: `/company-portal/${company.id}` })}
          >
            <Group justify="space-between" align="center" wrap="nowrap">
              <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                <IconBuilding size={24} />
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text fw={600}>{company.name}</Text>
                  <Text size="sm" c="dimmed">
                    <Trans>{company.properties?.length || 0} properties</Trans>
                  </Text>
                </Stack>
              </Group>
              <Group gap="md" wrap="nowrap">
                <PortfolioCsatChip customerId={company.id} />
                <IconChevronRight
                  size={20}
                  color="var(--mantine-color-gray-5)"
                />
              </Group>
            </Group>
          </Card>
        ))}
      </Stack>
    </AppPage>
  )
}

// Own component so each card gets its own CSAT query (hooks can't run in a loop).
function PortfolioCsatChip({ customerId }: { customerId: number }) {
  const { data: csat } = csatHooks.useForCustomer(customerId)
  return (
    <CsatChip
      avgRating={csat?.portfolio.avgRating ?? null}
      count={csat?.portfolio.count ?? 0}
    />
  )
}

export const Route = createFileRoute('/_app/company-portal/')({
  component: CompanyPortalIndex,
})
